import authService from '@/api/authService';
import receiptApi from '@/api/receiptApi';
import userApi from '@/api/userApi';
import { Desktop } from '@/components/breakpoints';
import ChildNavigationWidget from '@/components/childNavigationWidget';
import DuplicateReceiptWidget from '@/components/duplicateReceiptWidget';
import FileUploader from '@/components/fileUploader';
import History from '@/components/history/history';
import MealParticipants from '@/components/mealParticipants';
import MilageAllowanceEditor from '@/components/milageAllowanceEditor';
import NavigationBar from '@/components/navigationBar';
import NavigationWidget from '@/components/navigationWidget';
import { PageTitle } from '@/components/pageTitle';
import StateWidget from '@/components/stateWidget';
import useLocalizeError from '@/hooks/useLocalizeError';
import { EditorContextProvider } from '@/lib/editorContext';
import { useHandleError } from '@/lib/errors';
import ReceiptEditorData from '@/pages/receiptEditor/receiptEditorData';
import ReceiptEditorFile from '@/pages/receiptEditor/receiptEditorFile';
import { ReceiptEditorForm } from '@/pages/receiptEditor/receiptEditorForm';
import ReceiptReadonlyData from '@/pages/receiptEditor/receiptReadonlyData';
import { ActionIcon, Box, Breadcrumbs, Card, Container, Skeleton } from '@mantine/core';
import { IconHome } from '@tabler/icons-react';
import { createFileRoute, Link, useLocation } from '@tanstack/react-router';
import pinEmpty from 'common/assets/icons/pin_empty.svg';
import pinFull from 'common/assets/icons/pin_full.svg';
import { hasClaim } from 'common/helpers/claimsHelpers';
import { ReceiptDraft } from 'common/types/receiptDraft';
import { deepEqual } from 'cross-state';
import { pick } from 'lodash';
import { useState } from 'react';
import { useTranslator } from 'translations';

export const Route = createFileRoute('/_auth/_home/r/$receiptId')({
    component: RouteComponent,
    async loader({ params: { receiptId } }) {
        await receiptApi.get(receiptId).get();
    },
});

function RouteComponent() {
    const t = useTranslator();
    const location = useLocation();
    const { receiptId } = Route.useParams();
    const [data, error] = receiptApi.get(receiptId).useCache();
    useHandleError(error);
    const { receipt, bucket } = data ?? {};
    const localizeError = useLocalizeError();

    const [owner] = userApi.get(bucket?.ownerId ?? '').useCache({ disabled: !bucket?.ownerId });
    const [appLogin] = authService.appLogin.useCache();

    const [sticky, setSticky] = useState(false);
    const isChild = bucket?.type === 'trip';
    const canWrite =
        !!bucket &&
        hasClaim(appLogin?.user, 'write', { forState: bucket.state, forUser: bucket.ownerId });
    const canDelete =
        !!bucket &&
        hasClaim(appLogin?.user, 'delete', { forState: bucket.state, forUser: bucket.ownerId });

    return (
        <PageTitle key={receiptId} title={t('receiptEditor.header', { receiptId: receiptId })}>
            <ReceiptEditorForm.Form
                original={data}
                css={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
                localizeError={localizeError}
                initiallyTriggerValidations={location.state.validate}
                autoSave={{
                    enabled: canWrite,
                    equals: (a, b) =>
                        deepEqual(
                            pick(a.receipt, ReceiptDraft.keyof().options),
                            pick(b.receipt, ReceiptDraft.keyof().options),
                            {
                                undefinedEqualsAbsent: true,
                            },
                        ),
                    async save(draft, _prev, form) {
                        if (!form.original?.receipt.id) {
                            return;
                        }

                        const data = ReceiptDraft.parse(draft.receipt);
                        await receiptApi.update(form.original.receipt.id, data);
                    },
                    debounce: 5_000,
                    resetAfterSave: true,
                    validateBeforeSave: false,
                }}
            >
                <EditorContextProvider canWrite={canWrite} canDelete={canDelete}>
                    <NavigationBar
                        breadcrumbs={
                            owner && receipt ? (
                                <Breadcrumbs>
                                    <Link to="/">
                                        <IconHome />
                                    </Link>

                                    <Link to="/" search={{ ownerId: [owner.id] }}>
                                        {owner.name}
                                    </Link>

                                    {bucket?.type === 'trip' && (
                                        <Link to="/t/$tripId" params={{ tripId: bucket.id }} state>
                                            {t('tripEditor.header', { tripId: bucket.id })}
                                        </Link>
                                    )}

                                    <Link to="." state>
                                        {t('receiptEditor.header', { receiptId: receipt.id })}
                                    </Link>
                                </Breadcrumbs>
                            ) : (
                                <Skeleton width={300} />
                            )
                        }
                        additionalContent={
                            !receipt || !bucket ? null : bucket?.type === 'trip' ? (
                                <ChildNavigationWidget trip={bucket} receipt={receipt} />
                            ) : (
                                <NavigationWidget bucket={bucket} />
                            )
                        }
                        justifyAdditionalContent={bucket?.type === 'standalone' ? 'end' : 'center'}
                    />

                    <Box
                        css={{
                            flex: 1,
                            padding: 'var(--mantine-spacing-md)',
                            display: 'grid',
                            grid: `
                                ${isChild ? '' : `'state content' max-content`}
                                'data content' max-content
                                'history content' minmax(0, 1fr)
                                / 450px minmax(0, 1fr)
                            `,
                            gap: 'var(--mantine-spacing-md)',

                            '@media (max-width: 900px)': {
                                grid: `
                                    ${isChild ? '' : `'state'`}
                                    'content'
                                    'data'
                                    'history'
                                    / minmax(0, 1fr)
                                `,
                            },
                        }}
                    >
                        {bucket?.type === 'standalone' && (
                            <Card
                                withBorder
                                css={{
                                    gridArea: 'state',
                                    padding: 'var(--mantine-spacing-sm)',
                                    backgroundColor: 'var(--mantine-color-primary-filled)',
                                    color: 'var(--mantine-color-white)',
                                }}
                            >
                                <StateWidget bucket={bucket} />
                            </Card>
                        )}

                        <Card
                            withBorder
                            css={{
                                gridArea: 'history',
                                alignSelf: 'start',
                            }}
                        >
                            {receipt && bucket ? (
                                <History bucket={bucket} receipt={receipt} />
                            ) : (
                                <Skeleton variant="rectangular" height={400} />
                            )}
                        </Card>

                        <Card
                            withBorder
                            shadow={sticky ? 'md' : undefined}
                            css={{
                                position: sticky ? 'sticky' : 'relative',
                                gridArea: 'data',
                                maxHeight: sticky ? '80vh' : 'none',
                                overflowY: 'auto',
                                background: 'white',
                                top: sticky ? 'var(--app-shell-header-height)' : 0,
                                transform: sticky ? 'var(--app-shell-header-transform)' : undefined,
                                transition: `
                                    transform var(--app-shell-transition-duration) var(--app-shell-transition-timing-function),
                                    box-shadow 300ms ease`,
                                zIndex: 1,

                                '@media (max-width: 600px)': {
                                    position: 'relative',
                                    top: 0,
                                },
                            }}
                        >
                            {!canWrite ? <ReceiptReadonlyData /> : <ReceiptEditorData />}

                            <Desktop>
                                <ActionIcon
                                    css={{
                                        position: 'absolute',
                                        top: 'var(--mantine-spacing-md)',
                                        right: 'var(--mantine-spacing-md)',
                                    }}
                                    size="sm"
                                    color="primary"
                                    onClick={() => setSticky((s) => !s)}
                                    variant="subtle"
                                >
                                    {sticky ? (
                                        <img src={pinFull} css={{ width: '1.8em' }} />
                                    ) : (
                                        <img src={pinEmpty} css={{ width: '1.8em' }} />
                                    )}
                                </ActionIcon>
                            </Desktop>
                        </Card>

                        <Container
                            size="md"
                            css={{
                                gridArea: 'content',
                                width: '100%',
                                alignSelf: 'start',
                                rowGap: 'var(--mantine-spacing-xl)',
                            }}
                        >
                            <ReceiptEditorForm.Field
                                name="receipt.duplicates"
                                render={(props) => (
                                    <DuplicateReceiptWidget
                                        {...props}
                                        bucket={bucket}
                                        receipt={receipt}
                                    />
                                )}
                            />

                            <ReceiptEditorForm.FormState
                                selector={(form) => form.draft.receipt.type}
                            >
                                {(type) =>
                                    type === 'meal' ? (
                                        <MealParticipants />
                                    ) : type === 'milageAllowance' ? (
                                        <MilageAllowanceEditor />
                                    ) : null
                                }
                            </ReceiptEditorForm.FormState>

                            <ReceiptEditorFile />
                        </Container>
                    </Box>
                </EditorContextProvider>
            </ReceiptEditorForm.Form>

            <FileUploader active={canWrite} receiptId={receiptId} />
        </PageTitle>
    );
}
